import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../context/LanguageContext';
import { motion } from 'framer-motion';
import {
  Ruler,
  UserCheck,
  Building2,
  FileCheck,
  Package,
  CalendarCheck,
  Star,
  ArrowRight,
  Search,
} from 'lucide-react';
import { processStepsApi } from '../../services/api';

const iconMap = {
  ruler: Ruler,
  'user-check': UserCheck,
  building2: Building2,
  'file-check': FileCheck,
  package: Package,
  'calendar-check': CalendarCheck,
  star: Star,
};

const ProcessSection = () => {
  const { t } = useTranslation();
  const { currentLang } = useLanguage();
  const [steps, setSteps] = useState([]);

  useEffect(() => {
    processStepsApi.getAll()
      .then((res) => setSteps(res.data))
      .catch(() => {});
  }, []);

  const padded = String(steps.length > 0 ? steps.length : 0).padStart(2, '0');

  return (
    <section data-testid="process-section" className="section-padding bg-brand-dark text-white grain">
      <div className="container-custom relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <span className="label-text mb-4 block">{t('process.label')}</span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6">
            {t('process.title')}
          </h2>
          <p className="text-white/60 text-lg">
            {t('process.description')}
          </p>
        </motion.div>

        {/* Steps grid — 3 per row */}
        <div className="grid md:grid-cols-3 gap-8">
          {steps.map((step, index) => {
            const IconComponent = iconMap[step.icon] || Star;
            const title = currentLang === 'ar' ? step.title_ar : step.title_en;
            const description = currentLang === 'ar' ? step.description_ar : step.description_en;
            const number = String(index + 1).padStart(2, '0');
            const isLastInRow = index % 3 === 2;

            return (
              <motion.div
                key={step.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (index % 3) * 0.15 }}
                data-testid={`process-step-${index + 1}`}
                className="relative group"
              >
                {/* Connector line — not after the last item in each row */}
                {!isLastInRow && index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-16 start-full w-full h-px bg-white/10 z-0" />
                )}

                <div className="relative z-10 p-8 border border-white/10 bg-white/5 backdrop-blur-sm hover:border-gold/50 transition-colors duration-500">
                  {/* Step number watermark */}
                  <span className="text-gold text-6xl font-bold opacity-20 absolute top-4 end-4">
                    {number}
                  </span>

                  {/* Icon */}
                  <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mb-6 group-hover:bg-gold/20 transition-colors">
                    <IconComponent className="text-gold" size={28} />
                  </div>

                  <h3 className="text-xl font-semibold mb-4">{title}</h3>
                  <p className="text-white/60 leading-relaxed">{description}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-14"
        >
          <Link
            to="/start"
            data-testid="process-cta-start"
            className="inline-flex items-center gap-3 bg-gold text-charcoal-deep font-bold px-10 py-5 text-lg hover:opacity-90 transition-opacity"
          >
            {t('orderJourney.cta')}
            <ArrowRight size={22} className={currentLang === 'ar' ? 'rotate-180' : ''} />
          </Link>

          <Link
            to="/follow-up"
            data-testid="process-cta-follow-up"
            className="inline-flex items-center gap-3 border-2 border-white/25 text-white font-semibold px-8 py-[18px] text-base hover:border-gold/60 hover:text-gold hover:bg-gold/5 transition-all duration-300"
          >
            <Search size={18} />
            {t('orderJourney.followUp')}
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default ProcessSection;
